import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellRing, AlertCircle, ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { api, errMsg, money } from "@/lib/api";

const DAY = 86400000;
export const daysSince = (iso?: string | null) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / DAY) : 0);

export function needsFollowUp(q: any) {
  return q.status === "sent" && !q.viewed_at && daysSince(q.sent_at || q.updated_at) >= 7;
}

export function actionFor(q: any, isAdmin: boolean): { label: string; tone: string } | null {
  switch (q.status) {
    case "revision_requested": return { label: `Homeowner asked for changes${q.revision_request?.message ? `: “${q.revision_request.message.slice(0, 60)}”` : ""} — create a revision`, tone: "text-orange-700 bg-orange-50" };
    case "approved": return { label: "Approved — ready to send to the homeowner", tone: "text-emerald-700 bg-emerald-50" };
    case "rejected": return { label: "Declined — re-quote or close out", tone: "text-red-700 bg-red-50" };
    case "internal_review": return isAdmin ? { label: "Awaiting your approval", tone: "text-amber-700 bg-amber-50" } : null;
    case "draft": return q.total === 0 ? { label: "Draft has no pricing yet", tone: "text-gray-700 bg-gray-100" } : null;
    default: return q.last_reply?.body && q.last_reply?.unanswered !== false ? { label: `Homeowner texted: “${String(q.last_reply.body).slice(0, 70)}”`, tone: "text-emerald-800 bg-emerald-50" } : null;
  }
}

export function ActionPanels({ quotes, isAdmin }: { quotes: any[]; isAdmin: boolean }) {
  const nav = useNavigate();
  const [digesting, setDigesting] = useState(false);
  const sendDigest = async () => {
    setDigesting(true);
    try {
      const { data } = await api.post("/reminders/digest/send");
      if (data.sent.length) toast.success(`Digest emailed to ${data.sent.join(", ")}`);
      else toast.info(data.quotes ? `Digest prepared for ${data.groups} rep(s) but not emailed: ${data.skipped[0]?.reason || "no recipients"}` : "No quotes need a follow-up right now");
    } catch (e) { toast.error(errMsg(e)); } finally { setDigesting(false); }
  };
  const followUps = quotes.filter(needsFollowUp).sort((a, b) => daysSince(b.sent_at) - daysSince(a.sent_at));
  const actions = quotes.map((q) => ({ q, a: actionFor(q, isAdmin) })).filter((x) => x.a);
  if (!followUps.length && !actions.length) return null;
  return (
    <div className="mt-8 grid gap-4 lg:grid-cols-2">
      {actions.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm" data-testid="needs-action-panel">
          <div className="flex items-center gap-2 border-b border-gray-200 px-5 py-3"><AlertCircle size={16} className="text-blue-700" /><h2 className="text-sm font-semibold text-gray-900">Needs your action</h2><span className="ml-auto rounded-full bg-blue-700 px-2 py-0.5 text-xs font-semibold text-white" data-testid="needs-action-count">{actions.length}</span></div>
          <ul className="divide-y divide-gray-100">
            {actions.slice(0, 6).map(({ q, a }) => (
              <li key={q.id} data-testid={`action-item-${q.id}`} onClick={() => nav(`/quotes/${q.id}`)} className="flex cursor-pointer items-center gap-3 px-5 py-3 hover:bg-gray-50">
                <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium text-gray-900">{q.homeowner_name} <span className="font-normal text-gray-500">· {money(q.total)}</span></div><div className={`mt-1 inline-block rounded px-1.5 py-0.5 text-xs ${a!.tone}`}>{a!.label}</div></div>
                <StatusBadge status={q.status} /><ArrowRight size={14} className="text-gray-400" />
              </li>
            ))}
          </ul>
        </div>
      )}
      {followUps.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-white shadow-sm" data-testid="follow-up-panel">
          <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-5 py-3 rounded-t-lg"><BellRing size={16} className="text-amber-600" /><h2 className="text-sm font-semibold text-gray-900">Follow-up reminders</h2><span className="text-xs text-amber-700">sent 7+ days ago, never opened</span>
            {isAdmin && <button data-testid="send-digest-btn" disabled={digesting} onClick={sendDigest} className="ml-2 inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100" title="Reps get this automatically every morning at 9am"><Mail size={12} />{digesting ? "Sending…" : "Send digest now"}</button>}
            <span className="ml-auto rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white" data-testid="follow-up-count">{followUps.length}</span></div>
          <ul className="divide-y divide-gray-100">
            {followUps.slice(0, 6).map((q) => (
              <li key={q.id} data-testid={`follow-up-item-${q.id}`} onClick={() => nav(`/quotes/${q.id}`)} className="flex cursor-pointer items-center gap-3 px-5 py-3 hover:bg-gray-50">
                <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium text-gray-900">{q.homeowner_name} <span className="font-normal text-gray-500">· {money(q.total)}</span></div><div className="text-xs text-gray-500">Sent {daysSince(q.sent_at || q.updated_at)} days ago · not viewed yet — resend the link or call</div></div>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">{daysSince(q.sent_at || q.updated_at)}d</span><ArrowRight size={14} className="text-gray-400" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
